/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Keyword } from "../directives/content-editable.directive";
import { Injectable } from "@angular/core";

import { Event } from '../models/event';
import { Property } from '../models/property';
import { PropertyType, PropertyTypeUtil } from '../models/property-type';

@Injectable()
export class KeywordService {
  
  constructor(
  ) {}
  
  getAvailableKeywords(): Keyword[] {
    const rv: Keyword[] = [];

    let kw = new Keyword();
    kw.name = "LOCATION";
    kw.classes = ['fa', 'fa-map'];
    kw.token = "${location}";
    kw.button = `<button style="height: 27px; margin: 3px; line-height: normal;" class="additionalInfoButton" contenteditable="false" disabled="true"><em class="fa fa-map"></em>LOCATION</button>`;
    rv.push(kw);

    kw = new Keyword();
    kw.name = "ZONE";
    kw.classes = ['fa', 'fa-bullseye'];
    kw.token = "${zone}";
    kw.button = `<button style="height: 27px; margin: 3px;" class="additionalInfoButton" contenteditable="false" disabled="true"><em class="fa fa-bullseye"></em>ZONE</button>`;
    rv.push(kw);

    kw = new Keyword();
    kw.name = "DATE";
    kw.classes = ['fa', 'fa-calendar'];
    kw.token = "${date}";
    kw.button = `<button style="height: 27px; margin: 3px;" class="additionalInfoButton" contenteditable="false" disabled="true"><em class="fa fa-calendar"></em>DATE</button>`;
    rv.push(kw);

    kw = new Keyword();
    kw.name = "TIME";
    kw.classes = ['fa', 'fa-clock-o'];
    kw.token = "${time}";
    kw.button = `<button style="height: 27px; margin: 3px;" class="additionalInfoButton" contenteditable="false" disabled="true"><em class="fa fa-clock-o"></em>TIME</button>`;
    rv.push(kw);

    kw = new Keyword();
    kw.name = "EVENT DIALOG NAME";
    kw.classes = ['fa', 'fa-comment-o'];
    kw.token = "${dialog_name}";
    kw.button = `<button style="height: 27px; margin: 3px;" class="additionalInfoButton" contenteditable="false" disabled="true"><em class="fa fa-comment-o"></em>EVENT DIALOG NAME</button>`;
    rv.push(kw);

    kw = new Keyword();
    kw.name = "EVENT BUTTON CODE";
    kw.classes = ['fa', 'fa-fa-circle'];
    kw.token = "${button_code}";
    kw.button = `<button style="height: 27px; margin: 3px;" class="additionalInfoButton" contenteditable="false" disabled="true"><em class="fa fa-circle"></em>EVENT BUTTON CODE</button>`;
    rv.push(kw);

    kw = new Keyword();
    kw.name = "EVENT DIALOG ID";
    kw.classes = ['fa', 'fa-commenting-o'];
    kw.token = "${dialog_inst_id}";
    kw.button = `<button style="height: 27px; margin: 3px;" class="additionalInfoButton" contenteditable="false" disabled="true"><em class="fa fa-commenting-o"></em>EVENT DIALOG ID</button>`;
    rv.push(kw);

    return rv;
  }
  
  getAvailableKeywordsForEvent(event: Event): Keyword[] {
    const rv: Keyword[] = [];

    //
    // default keywords
    let kw = new Keyword();
    kw.name = "LOCATION";
    kw.classes = ['fa', 'fa-map'];
    kw.token = "${location}";
    kw.button = this.getButtonHtml("fa fa-map", "LOCATION");
    rv.push(kw);

    kw = new Keyword();
    kw.name = "ZONE";
    kw.classes = ['fa', 'fa-bullseye'];
    kw.token = "${zone}";
    kw.button = this.getButtonHtml("fa fa-bullseye", "ZONE");
    rv.push(kw);

    kw = new Keyword();
    kw.name = "DATE";
    kw.classes = ['fa', 'fa-calendar'];
    kw.token = "${date}";
    kw.button = this.getButtonHtml("fa fa-calendar", "DATE");
    rv.push(kw);

    kw = new Keyword();
    kw.name = "TIME";
    kw.classes = ['fa', 'fa-clock-o'];
    kw.token = "${time}";
    kw.button = this.getButtonHtml("fa fa-clock-o", "TIME");
    rv.push(kw);

    //
    // event based keywords
    if (event != null) {
      for (const property of event.properties) {
          if (!PropertyTypeUtil.isReference(property.type)) {
              kw = new Keyword();
              kw.name = property.name;
              kw.classes = ['fa', 'fa fa-dot-circle'];
              kw.token = "${" + property.property + "}";
              kw.button = this.getButton(property);
              
              rv.push(kw);
          } else {
              //
              // add name and type i.e. corresponding entity name
              kw = new Keyword();
              kw.name = property.name + " name";
              kw.classes = ['fa', 'fa-bullseye'];
              kw.token = "${" + property.property + "._name}";
              kw.button = this.getButton(property, "name");
              rv.push(kw);
              
              kw = new Keyword();
              kw.name = property.name + " type";
              kw.classes = ['fa', 'fa-bullseye'];
              kw.token = "${" + property.property + "._type}";
              kw.button = this.getButton(property, "type");
              rv.push(kw);            
          }
      }
    }
      
    return rv;
  }
    
  private getButton(property: Property, referenceProperty?: string): string {
    const label = property.name + (referenceProperty ? " " + referenceProperty : "");
    const clazz = PropertyTypeUtil.getIconClass(property.type);
    return this.getButtonHtml(clazz, label);
  }

  private getButtonHtml(iconClass: string, label: string): string {
    /*
     * Trying to get this to work for the following browsers: Chrome, Firefox, Edge
     *
     * Main Lessons: 
     *
     * Edge cannot handle HTML elements inside whatever HTML element is used for the main Token.
     * Tried the following outer HTML elements: "<button>, <span>, <div>", using these inner HTML elements: "<em>, <span>".
     * It seems to ignore any "contenteditable" flag and lets the User delete just the inner HTML element. 
     * 
     * Both Firefox and Edge still have some small issues when using a single HTML element, but it's not as bad...
     * 
     * Chrome is behaving fine no matter what I try...
     * 
     * For now, going with the single "<span>" element approach.  Spending too much time on this.
     */

    /*
     * Firefox is adding a <br> before the <div> in certain cases (Recreate it: From Blank Message: Type in "Asset: '[Asset Name Token]'", then delete the "'" before the token)
     */
    //return `<div class="${iconClass} additionalInfoButton" style="display: inline;" contenteditable="false">${label}</div>`;
        
    /*
     * Firefox is not able to delete the Token using backspace if there are no characters after it, and using delete if there are no characters before it
     *
     * Edge can delete the Tokens fine in most cases, but if you delete the token then start typing, it treats it as being in a "<font>" element styles like 
     * the button. 
     */
    return `<span class="${iconClass} additionalInfoButton" style="height: 27px; margin: 3px;" contenteditable="false">${label}</span>`;

    /*
     * Edge doesn't add the "<font>" element, but allows editing the label...
     */
    //return `<span contenteditable="false">${label}</span>`;
    
    /*
     * Firefox is not able to delete the Token using backspace if there are no characters after it, and using delete if there are no characters before it
     *
     * Edge can delete the Tokens fine in most cases, but if you delete the token then start typing, it treats it as being in a "<font>" element styles like 
     * the button. 
     */
    //return `<button disabled="true" class="${iconClass} additionalInfoButton" style="height: 27px; margin: 3px;" contenteditable="false">${label}</button>`;



    //Results in Edge deleting the inner <em>
    //return `<button disabled="true" class="${iconClass} additionalInfoButton" style="height: 27px; margin: 3px;" contenteditable="false"><em class="${iconClass}"></em>${label}</button>`;
    
    //Results in Edge deleting the inner <span> 
    //return `<div class="additionalInfoButton" style="height: 27px; margin: 3px; display: inline;" contenteditable="false"><span class="${iconClass}" contenteditable="false"></span>${label}</div>`;
  }

}
